/** @module @liquidefy/code-editor/themes/glass/backdrop @version 1.0.0 @license LQD Research LLC */
/** @rust glass_backdrop.rs — false; 4/2/26; sm */

import { hexToOKLCH, oklchToHex } from '../color/oklch.js';

// ═══ §1 TYPES ═══

/** The two ground modes — dark and light differ only in the ground layer. */
export type GroundMode = 'dark' | 'light';

/**
 * A ground layer defines the background canvas and text palette for one mode.
 * All glass surfaces float above this layer. Switching modes means swapping
 * the ground layer — no surface definitions need to change.
 */
export interface GroundLayer {
  readonly mode: GroundMode;
  readonly primary: string;         // main background hex
  readonly secondary: string;       // slightly offset for contrast
  readonly tertiary: string;        // third level (deep insets)
  readonly textPrimary: string;     // main text color
  readonly textSecondary: string;   // muted text
  readonly textTertiary: string;    // very muted / disabled text
  readonly glassTint: string;       // color used to tint glass surfaces
  readonly glassBorder: string;     // color used for glass borders
}

/** Holds both dark and light ground layers plus the active mode. */
export interface GroundConfig {
  readonly dark: GroundLayer;
  readonly light: GroundLayer;
  readonly activeMode: GroundMode;
}

/** Gradient background specification for the viewport ground. */
export interface GradientBackground {
  readonly type: 'solid' | 'linear' | 'radial' | 'mesh';
  readonly colors: string[];        // hex colors
  readonly angle?: number;          // degrees (for linear/mesh)
  readonly positions?: number[];    // color stop positions (0-1)
}

/** Quick summary of the active ground state. */
export interface GroundSummaryResult {
  readonly mode: GroundMode;
  readonly primaryBg: string;
  readonly primaryText: string;
}

// ═══ §2 DEFAULT GROUND LAYERS ═══

/**
 * Deep near-black backgrounds with cool blue undertones.
 * primary   #0a0a0f — deepest, faint blue-purple cast
 * secondary #0f0f17 — slightly lifted for sidebar distinction
 * tertiary  #141420 — third level for nested insets
 * Text: near-white (#e8e8ed) → muted gray (#a0a0b0) → dim (#606070)
 * Glass: very dark blue-purple tint, white borders
 */
export function createDefaultDarkGround(): GroundLayer {
  return {
    mode: 'dark',
    primary:       '#0a0a0f',
    secondary:     '#0f0f17',
    tertiary:      '#141420',
    textPrimary:   '#e8e8ed',
    textSecondary: '#a0a0b0',
    textTertiary:  '#606070',
    glassTint:     '#1a1a2e',
    glassBorder:   '#ffffff',
  };
}

/**
 * Soft warm whites with neutral undertones.
 * primary   #fafafa — near-pure white
 * secondary #f0f0f5 — slightly cooler off-white
 * tertiary  #e5e5eb — light gray for nested containers
 * Text: near-black (#1a1a1f) → medium gray (#505060) → light (#909098)
 * Glass: warm light gray tint, dark borders
 */
export function createDefaultLightGround(): GroundLayer {
  return {
    mode: 'light',
    primary:       '#fafafa',
    secondary:     '#f0f0f5',
    tertiary:      '#e5e5eb',
    textPrimary:   '#1a1a1f',
    textSecondary: '#505060',
    textTertiary:  '#909098',
    glassTint:     '#d0d0d8',
    glassBorder:   '#000000',
  };
}

// ═══ §3 GROUND CONFIG ═══

/**
 * Create a GroundConfig, merging optional partial overrides into defaults.
 * Active mode defaults to 'dark'. The mode field on each layer is forced
 * to the correct value regardless of what the partial provides.
 */
export function createGroundConfig(
  darkGround?: Partial<GroundLayer>,
  lightGround?: Partial<GroundLayer>,
): GroundConfig {
  const dark: GroundLayer = { ...createDefaultDarkGround(), ...darkGround, mode: 'dark' };
  const light: GroundLayer = { ...createDefaultLightGround(), ...lightGround, mode: 'light' };
  return { dark, light, activeMode: 'dark' };
}

/** Get the ground layer for the currently active mode. */
export function getActiveGround(config: GroundConfig): GroundLayer {
  return config.activeMode === 'dark' ? config.dark : config.light;
}

/** Get the ground layer for the inactive mode. */
export function getInactiveGround(config: GroundConfig): GroundLayer {
  return config.activeMode === 'dark' ? config.light : config.dark;
}

/** Switch to a specific mode. No-op if already in that mode. */
export function switchMode(config: GroundConfig, mode: GroundMode): GroundConfig {
  if (config.activeMode === mode) return config;
  return { ...config, activeMode: mode };
}

/** Toggle between dark and light mode. */
export function toggleMode(config: GroundConfig): GroundConfig {
  return { ...config, activeMode: config.activeMode === 'dark' ? 'light' : 'dark' };
}

/** Replace the dark ground layer, preserving light and active mode. */
export function setDarkGround(config: GroundConfig, dark: GroundLayer): GroundConfig {
  return { ...config, dark: { ...dark, mode: 'dark' } };
}

/** Replace the light ground layer, preserving dark and active mode. */
export function setLightGround(config: GroundConfig, light: GroundLayer): GroundConfig {
  return { ...config, light: { ...light, mode: 'light' } };
}

/** Partially update the dark ground layer. */
export function updateDarkGround(config: GroundConfig, patch: Partial<GroundLayer>): GroundConfig {
  return setDarkGround(config, { ...config.dark, ...patch, mode: 'dark' });
}

/** Partially update the light ground layer. */
export function updateLightGround(config: GroundConfig, patch: Partial<GroundLayer>): GroundConfig {
  return setLightGround(config, { ...config.light, ...patch, mode: 'light' });
}

// ═══ §4 CSS GENERATION ═══

/**
 * Convert a GroundLayer into CSS custom properties.
 * Produces: --ground-mode, --ground-primary, --ground-text-primary, etc.
 */
export function groundToCSS(ground: GroundLayer): Record<string, string> {
  return {
    '--ground-mode':           ground.mode,
    '--ground-primary':        ground.primary,
    '--ground-secondary':      ground.secondary,
    '--ground-tertiary':       ground.tertiary,
    '--ground-text-primary':   ground.textPrimary,
    '--ground-text-secondary': ground.textSecondary,
    '--ground-text-tertiary':  ground.textTertiary,
    '--ground-glass-tint':     ground.glassTint,
    '--ground-glass-border':   ground.glassBorder,
  };
}

/** Generate CSS variables for the active ground in a config. */
export function activeGroundToCSS(config: GroundConfig): Record<string, string> {
  return groundToCSS(getActiveGround(config));
}

// ═══ §5 GRADIENT BACKGROUNDS ═══

/**
 * Create a gradient background from the ground layer's palette.
 * solid  — single primary color
 * linear — primary → secondary at angle (default 180deg)
 * radial — primary center → tertiary edge
 * mesh   — two layered linear gradients using all three colors
 */
export function createGradientBackground(
  ground: GroundLayer,
  type: GradientBackground['type'],
): GradientBackground {
  switch (type) {
    case 'solid':
      return { type, colors: [ground.primary] };
    case 'linear':
      return { type, colors: [ground.primary, ground.secondary], angle: 180, positions: [0, 1] };
    case 'radial':
      return { type, colors: [ground.primary, ground.tertiary], positions: [0, 1] };
    case 'mesh':
      return {
        type,
        colors: [ground.primary, ground.secondary, ground.tertiary],
        positions: [0, 0.5, 1],
        angle: 135,
      };
  }
}

/** Create a gradient with custom parameters. */
export function createCustomGradient(
  type: GradientBackground['type'],
  colors: string[],
  angle?: number,
  positions?: number[],
): GradientBackground {
  return { type, colors, angle, positions };
}

/** Convert a GradientBackground to a CSS background value string. */
export function gradientToCSS(gradient: GradientBackground): string {
  switch (gradient.type) {
    case 'solid':
      return gradient.colors[0] ?? '#000000';

    case 'linear': {
      const angle = gradient.angle ?? 180;
      return `linear-gradient(${angle}deg, ${buildStops(gradient.colors, gradient.positions)})`;
    }

    case 'radial':
      return `radial-gradient(ellipse at center, ${buildStops(gradient.colors, gradient.positions)})`;

    case 'mesh': {
      const angle = gradient.angle ?? 135;
      const perpAngle = (angle + 90) % 360;
      const c = gradient.colors;
      if (c.length < 3) {
        return `linear-gradient(${angle}deg, ${buildStops(c, gradient.positions)})`;
      }
      const layer1 = `linear-gradient(${angle}deg, ${c[0]}, transparent)`;
      const layer2 = `linear-gradient(${perpAngle}deg, ${c[1]}, ${c[2]})`;
      return `${layer1}, ${layer2}`;
    }
  }
}

function buildStops(colors: string[], positions?: number[]): string {
  return colors
    .map((c, i) => {
      if (positions !== undefined && positions[i] !== undefined) {
        return `${c} ${Math.round(positions[i]! * 100)}%`;
      }
      return c;
    })
    .join(', ');
}

// ═══ §6 DERIVE GROUND FROM SEED ═══

/**
 * Given a hex seed color, derive a full GroundLayer for the specified mode.
 * Uses OKLCH to extract the seed's hue, then generates backgrounds at
 * mode-appropriate lightness with very low chroma (near-neutral).
 *
 * Dark mode: L = 0.05/0.07/0.10 (near black), glassTint at L = 0.12
 * Light mode: L = 0.98/0.96/0.93 (near white), glassTint at L = 0.85
 *
 * Text colors use fixed palettes — readability over hue consistency.
 */
export function deriveGroundFromSeed(seed: string, mode: GroundMode): GroundLayer {
  const oklch = hexToOKLCH(seed);
  const hue = oklch.H;

  if (mode === 'dark') {
    return {
      mode: 'dark',
      primary:       oklchToHex({ L: 0.05, C: 0.005, H: hue }),
      secondary:     oklchToHex({ L: 0.07, C: 0.006, H: hue }),
      tertiary:      oklchToHex({ L: 0.10, C: 0.008, H: hue }),
      textPrimary:   '#e8e8ed',
      textSecondary: '#a0a0b0',
      textTertiary:  '#606070',
      glassTint:     oklchToHex({ L: 0.12, C: 0.010, H: hue }),
      glassBorder:   '#ffffff',
    };
  }

  return {
    mode: 'light',
    primary:       oklchToHex({ L: 0.98, C: 0.004, H: hue }),
    secondary:     oklchToHex({ L: 0.96, C: 0.005, H: hue }),
    tertiary:      oklchToHex({ L: 0.93, C: 0.006, H: hue }),
    textPrimary:   '#1a1a1f',
    textSecondary: '#505060',
    textTertiary:  '#909098',
    glassTint:     oklchToHex({ L: 0.85, C: 0.008, H: hue }),
    glassBorder:   '#000000',
  };
}

/** Derive a complete GroundConfig from a single seed color. */
export function deriveGroundConfigFromSeed(seed: string): GroundConfig {
  return {
    dark: deriveGroundFromSeed(seed, 'dark'),
    light: deriveGroundFromSeed(seed, 'light'),
    activeMode: 'dark',
  };
}

// ═══ §7 COMPARISON ═══

/** Check whether two ground layers are structurally equal. */
export function groundLayersEqual(a: GroundLayer, b: GroundLayer): boolean {
  return (
    a.mode === b.mode &&
    a.primary === b.primary &&
    a.secondary === b.secondary &&
    a.tertiary === b.tertiary &&
    a.textPrimary === b.textPrimary &&
    a.textSecondary === b.textSecondary &&
    a.textTertiary === b.textTertiary &&
    a.glassTint === b.glassTint &&
    a.glassBorder === b.glassBorder
  );
}

// ═══ §8 SUMMARY ═══

/** Quick summary of the active ground configuration. */
export function groundSummary(config: GroundConfig): GroundSummaryResult {
  const active = getActiveGround(config);
  return {
    mode: active.mode,
    primaryBg: active.primary,
    primaryText: active.textPrimary,
  };
}
